import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormModal } from "@/components/Settings/FormModal";
import { organizationsApi } from "@/apis";
import type { Organization } from "@/apis";

interface OrganizationFormState {
  organizationName: string;
  description: string;
  organizationLogo: string;
}

const EMPTY_FORM: OrganizationFormState = {
  organizationName: "",
  description: "",
  organizationLogo: "",
};

export function OrganizationsManager() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [modalError, setModalError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [form, setForm] = useState<OrganizationFormState>(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [existingLogoUrl, setExistingLogoUrl] = useState("");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const modalTitle = useMemo(
    () => (editing ? "Edit Organization" : "Create Organization"),
    [editing]
  );

  const loadOrganizations = async () => {
    setLoading(true);
    setPageError("");
    try {
      const data = await organizationsApi.list({ limit: 500 });
      setOrganizations(data.items ?? []);
    } catch (err) {
      setPageError(
        err instanceof Error ? err.message : "Failed to load organizations"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrganizations();
  }, []);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview("");
      return;
    }
    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setExistingLogoUrl("");
    setModalError("");
    setIsModalOpen(true);
  };

  const openEdit = (org: Organization) => {
    setEditing(org);
    setForm({
      organizationName: org.organizationName || "",
      description: org.description || "",
      organizationLogo: org.organizationLogoKey || "",
    });
    setLogoFile(null);
    setExistingLogoUrl(org.organizationLogo || "");
    setModalError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalError("");
    setIsModalOpen(false);
  };

  const uploadLogoIfNeeded = async (): Promise<string> => {
    if (!logoFile) {
      return form.organizationLogo;
    }
    const upload = await organizationsApi.uploadLogo(logoFile);
    return upload.logo_key;
  };

  const saveOrganization = async () => {
    const normalizedName = form.organizationName.trim();
    const normalizedDescription = form.description.trim();
    const hasLogo = Boolean(logoFile || form.organizationLogo || existingLogoUrl);

    if (!normalizedName) {
      setModalError("Organization Name is required.");
      return;
    }

    if (!hasLogo) {
      setModalError("Organization Logo is required.");
      return;
    }

    setIsSaving(true);
    setModalError("");
    try {
      const logoKey = await uploadLogoIfNeeded();
      const payload = {
        organizationName: normalizedName,
        description: normalizedDescription,
        organizationLogo: logoKey,
      };

      if (editing) {
        await organizationsApi.update(editing.organizationId, payload);
      } else {
        await organizationsApi.create(payload);
      }

      setIsModalOpen(false);
      setLogoFile(null);
      setExistingLogoUrl("");
      await loadOrganizations();
    } catch (err) {
      setModalError(
        err instanceof Error ? err.message : "Failed to save organization"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (org: Organization) => {
    setOrgToDelete(org);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirmOpen(false);
    setOrgToDelete(null);
  };

  const confirmDelete = async () => {
    if (!orgToDelete) return;

    setIsDeleting(true);
    setPageError("");
    try {
      await organizationsApi.remove(orgToDelete.organizationId);
      setDeleteConfirmOpen(false);
      setOrgToDelete(null);
      await loadOrganizations();
    } catch (err) {
      setPageError(
        err instanceof Error ? err.message : "Failed to delete organization"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl">Organizations</CardTitle>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Organization
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {pageError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!loading && organizations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No organizations found.</p>
        ) : null}

        {!loading && organizations.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Logo</th>
                  <th className="px-3 py-2 text-left font-medium">
                    Organization Name
                  </th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.organizationId} className="border-t">
                    <td className="px-3 py-2">
                      {org.organizationLogo ? (
                        <img
                          src={org.organizationLogo}
                          alt={`${org.organizationName} logo`}
                          className="h-8 w-8 rounded object-cover"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-2">{org.organizationName}</td>
                    <td className="px-3 py-2 max-w-md truncate">
                      {org.description || "-"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(org)}
                        >
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteConfirm(org)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>

      <FormModal
        title={modalTitle}
        description="Manage organization details."
        isOpen={isModalOpen}
        isSaving={isSaving}
        onClose={closeModal}
        onSubmit={saveOrganization}
      >
        {modalError ? (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {modalError}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Organization Name *</label>
            <Input
              value={form.organizationName}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  organizationName: e.target.value,
                }))
              }
              placeholder="Acme Corp"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              rows={3}
              placeholder="Short description of the organization"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Organization Logo *</label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
            />
            {(logoFile || form.organizationLogo || existingLogoUrl) && (
              <img
                src={logoPreview || existingLogoUrl}
                alt="Logo preview"
                className="mt-2 h-12 w-12 rounded object-cover"
              />
            )}
          </div>
        </div>
      </FormModal>

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Delete Organization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 flex flex-col justify-between h-full pb-6">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{orgToDelete?.organizationName}</strong>? This action
                cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}
